<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="720px"
    custom-class="kr-automation-dialog"
    :close-on-click-modal="false"
    :before-close="handleClose"
    append-to-body
  >
    <div slot="title" class="kr-automation-dialog__header">
      <div class="kr-automation-dialog__eyebrow-row">
        <span class="aq-form-label">KEY RESULT</span>
        <span v-if="demoMode" class="kr-automation-dialog__demo-badge" title="Demo mode: backend not deployed yet, responses are simulated.">
          DEMO MODE
        </span>
      </div>
      <div class="kr-automation-dialog__kr-title">{{ keyResult ? keyResult.title : '' }}</div>
      <div class="kr-automation-dialog__heading">Automate updates</div>
    </div>

    <div class="kr-automation-dialog__body">
      <section v-if="existingMetricSource" class="automation-management-bar">
        <div class="automation-management-bar__status">
          <span
            class="automation-management-bar__dot"
            :class="{ 'automation-management-bar__dot--paused': automationPaused }"
          />
          <div class="automation-management-bar__copy">
            <span class="automation-management-bar__label">
              {{ automationPaused ? 'Automation paused' : 'Automation active' }}
            </span>
            <span class="automation-management-bar__hint">
              {{ automationPaused
                ? 'Automatic updates will not run until you resume.'
                : 'This Key Result will update on its saved schedule.' }}
            </span>
          </div>
        </div>
        <div class="automation-management-bar__actions">
          <el-button
            size="mini"
            :loading="lifecycleBusy"
            @click="onToggleAutomation"
          >
            {{ automationPaused ? 'Resume automation' : 'Pause automation' }}
          </el-button>
          <el-button
            size="mini"
            type="danger"
            plain
            :disabled="lifecycleBusy"
            @click="onDeleteAutomation"
          >
            Delete automation
          </el-button>
        </div>
      </section>

      <!-- 1. PROMPT -->
      <section class="dialog-section">
        <div class="prompt-header">
          <div class="aq-form-label">WHAT SHOULD UPDATE THIS KEY RESULT?</div>
          <transition name="detected-fade">
            <span v-if="detectedToolkit" class="detected-pill" :key="detectedToolkit.slug">
              <AutomationToolkitMark :slug="detectedToolkit.slug" :name="detectedToolkit.name" :logo-url="detectedToolkit.logo_url" size="md" />
              <span class="detected-pill__label">{{ detectedToolkit.name }}</span>
              <span class="detected-pill__hint">detected</span>
            </span>
          </transition>
        </div>

        <div v-if="showRecipePills" class="example-chip-row">
          <span class="aq-form-label example-chip-row__label">{{ activeRecipePills.label }}</span>
          <button
            v-for="(ex, idx) in activeRecipePills.recipes"
            :key="idx"
            type="button"
            class="example-chip"
            :class="{ 'example-chip--toolkit': activeRecipePills.isToolkitSpecific }"
            @click="applyExample(ex)"
          >
            {{ ex.label }}
          </button>
        </div>

        <div class="prompt-input-wrap" :class="{ 'prompt-input-wrap--has-toolkit': !!activeToolkitSlug }">
          <div class="prompt-mirror" aria-hidden="true" v-html="highlightedPromptHtml" />
          <el-input
            v-model="prompt"
            type="textarea"
            :autosize="{ minRows: 3, maxRows: 6 }"
            :placeholder="placeholder"
            ref="promptInput"
            class="prompt-input"
          />
        </div>

        <div class="toolkit-row">
          <span class="aq-form-label toolkit-row__label">Or pull from</span>
          <button
            v-for="t in toolkitShortcuts"
            :key="t.slug"
            type="button"
            class="toolkit-tile"
            :class="{
              'toolkit-tile--active': detectedToolkit && detectedToolkit.slug === t.slug,
              'toolkit-tile--connected': isToolkitConnected(t.slug),
            }"
            :title="isToolkitConnected(t.slug) ? `${t.name} is already connected` : ''"
            @click="insertToolkit(t)"
          >
            <AutomationToolkitMark :slug="t.slug" :name="t.name" :logo-url="t.logo_url" size="md" />
            <span class="toolkit-tile__label">{{ t.name }}</span>
            <span
              v-if="isToolkitConnected(t.slug)"
              class="toolkit-tile__connected-dot"
              aria-label="Connected"
            />
          </button>
          <button type="button" class="toolkit-browse-btn" @click="browserVisible = true">
            <i class="el-icon-search" /> Browse {{ totalToolkitCount }}+ apps
          </button>
        </div>

        <div class="dialog-helper">Tip: mention the source app, the metric, and the date range. Any tool with an API connection works.</div>
        <div class="dialog-section__cta">
          <el-button
            type="primary"
            size="small"
            :loading="composing"
            :disabled="!prompt.trim() || composing"
            @click="onInterpret"
          >
            {{ composing ? 'Interpreting…' : (recipe ? 'Re-interpret' : 'Interpret') }}
          </el-button>
        </div>
      </section>

      <transition name="recipe-fade">
        <section v-if="interpretationIssue" key="interpretation-issue" class="dialog-section">
          <div class="interpretation-alert">
            {{ interpretationIssue }}
          </div>
        </section>
      </transition>

      <!-- 2. INTERPRETATION CARD -->
      <transition name="recipe-fade">
        <section v-if="recipe" key="recipe" class="dialog-section">
          <div class="interpretation-card">
            <div v-if="promptRecipeDrift" class="interpretation-card__drift">
              <happily-icon name="alert-triangle" :size="14" />
              <div class="interpretation-card__drift-text">
                <span class="interpretation-card__drift-title">
                  Recipe is querying <code>{{ promptRecipeDrift.recipeMetric }}</code>, not <code>{{ promptRecipeDrift.mentionedKeyword }}</code>.
                </span>
                <span class="interpretation-card__drift-sub">
                  Your prompt mentions “{{ promptRecipeDrift.mentionedKeyword }}” but the recipe was compiled with a different metric. Re-interpret with the metric spelled out (for example, “sum {{ promptRecipeDrift.mentionedKeyword }}”) to fix this.
                </span>
              </div>
            </div>
            <div class="interpretation-card__summary">
              {{ recipe.human_summary }}
            </div>
            <AutomationRecipeFlow
              :toolkit-slug="recipe.suggested_toolkit_slug"
              :toolkit-name="displayName"
              :toolkit-logo-url="activeToolkitLogo"
              :recipe="recipe.recipe"
            />
            <div v-if="recipeParamRows.length" class="interpretation-card__params">
              <div class="aq-form-label interpretation-card__params-label">Recipe will query</div>
              <div class="interpretation-card__params-grid">
                <div
                  v-for="row in recipeParamRows"
                  :key="row.key"
                  class="recipe-param-chip"
                >
                  <span class="recipe-param-chip__key">{{ row.key }}</span>
                  <span class="recipe-param-chip__val">{{ row.value }}</span>
                </div>
              </div>
            </div>
            <div v-if="recipe.needs_clarification && recipe.needs_clarification.length" class="interpretation-card__chips">
              <button
                v-for="(question, idx) in recipe.needs_clarification"
                :key="idx"
                type="button"
                class="clarification-chip"
                @click="onClarificationChip(question)"
              >
                {{ question }}
              </button>
            </div>
          </div>
        </section>
      </transition>

      <!-- 3. CONNECTION -->
      <section v-if="recipe" class="dialog-section">
        <!-- Happily-native: no OAuth step. The company is the implicit account. -->
        <div v-if="isHappilySource" class="connection-active connection-active--happily">
          <AutomationToolkitMark slug="happily" name="Happily" :logo-url="happilyLogoUrl" size="lg" />
          <div class="connection-active__text">
            <span class="connection-active__label">Happily</span>
            <span class="connection-active__sub">
              <span class="connection-active__dot" />
              Using your company data
            </span>
          </div>
        </div>
        <div v-else-if="!hasActiveConnection" class="connection-block">
          <div class="aq-form-label">CONNECT YOUR SOURCE</div>
          <p class="connection-block__copy">
            {{ connectCopy }}
          </p>
          <el-button type="primary" :loading="connecting" @click="onConnect">
            {{ connecting ? 'Opening…' : `Connect ${displayName}` }}
          </el-button>
          <div v-if="connecting" class="connection-block__waiting">
            Waiting for connection. Complete the steps in the new tab.
          </div>
        </div>
        <div v-else class="connection-active">
          <AutomationToolkitMark :slug="suggestedToolkitSlug" :name="displayName" :logo-url="activeToolkitLogo" size="lg" />
          <div class="connection-active__text">
            <span class="connection-active__label">{{ displayName }}</span>
            <span class="connection-active__sub">
              <span class="connection-active__dot" />
              {{ activeConnection.display_label }} · Connected
            </span>
          </div>
          <button v-if="connectionsForToolkit.length > 1" type="button" class="connection-active__switch" @click="cycleConnection">
            Switch
          </button>
        </div>
      </section>

      <!-- 4. PREVIEW -->
      <section v-if="recipe && hasActiveConnection" class="dialog-section">
        <div v-if="previewing" class="preview-loading">
          <i class="el-icon-loading" /> Running test…
        </div>
        <div v-else-if="preview" class="preview-block">
          <div class="preview-block__head">
            <span class="aq-form-label">CURRENT RESULT</span>
            <button
              v-if="hasPreviewIssue"
              type="button"
              class="preview-block__diagnose"
              @click="diagnosticVisible = true"
            >
              <happily-icon name="alert-triangle" :size="12" />
              <span>Diagnose</span>
            </button>
          </div>
          <div class="preview-block__value">
            {{ formatValue(preview.value) }}<span v-if="krUnit" class="preview-block__unit">{{ krUnit }}</span>
          </div>

          <button
            v-if="hasAggregationMismatch"
            type="button"
            class="preview-block__mismatch"
            @click="diagnosticVisible = true"
          >
            <happily-icon name="alert-triangle" :size="14" />
            <div class="preview-block__mismatch-text">
              <span class="preview-block__mismatch-title">
                {{ preview.source_summary.records_counted }} {{ preview.source_summary.records_counted === 1 ? 'record' : 'records' }} came back, but the {{ recipe.recipe.post_processing.aggregation || 'aggregation' }} produced 0.
              </span>
              <span class="preview-block__mismatch-sub">
                The field we tried to {{ recipe.recipe.post_processing.aggregation || 'sum' }} doesn't match the response. Diagnose to see the field comparison and fix.
              </span>
            </div>
            <span class="preview-block__mismatch-cta">Diagnose</span>
          </button>

          <div v-if="targetMeter" class="preview-block__meter">
            <div class="preview-block__meter-track">
              <div class="preview-block__meter-fill" :style="{ width: targetMeter.percent + '%' }" />
            </div>
            <div class="preview-block__meter-caption">
              <span>{{ targetMeter.percent }}% of target</span>
              <span class="preview-block__meter-target">target {{ formatValue(targetMeter.target) }}<span v-if="krUnit"> {{ krUnit }}</span></span>
            </div>
          </div>

          <!-- Per-owner breakdown for individual-mode KRs (any provider). -->
          <div v-if="ownerBreakdown && ownerBreakdown.length" class="preview-block__samples">
            <div class="preview-block__samples-head">
              <AutomationToolkitMark :slug="sampleRecordsToolkit" :name="displayName" :logo-url="activeToolkitLogo" size="sm" />
              <span class="aq-form-label">Per-owner breakdown ({{ ownerBreakdown.length }} {{ ownerBreakdown.length === 1 ? 'owner' : 'owners' }})</span>
            </div>
            <ul class="preview-block__samples-list">
              <li v-for="row in ownerBreakdown" :key="row.email" class="preview-block__sample">
                <span class="preview-block__sample-label">{{ row.label || row.email }}</span>
                <span class="preview-block__sample-value">{{ formatValue(row.value) }}<span v-if="krUnit"> {{ krUnit }}</span></span>
              </li>
            </ul>
          </div>

          <div v-else-if="sampleRecords && sampleRecords.length" class="preview-block__samples">
            <div class="preview-block__samples-head">
              <AutomationToolkitMark :slug="sampleRecordsToolkit" :name="displayName" :logo-url="activeToolkitLogo" size="sm" />
              <span class="aq-form-label">Showing {{ sampleRecords.length }} of {{ preview.source_summary.records_counted }} records</span>
            </div>
            <ul class="preview-block__samples-list">
              <li v-for="(rec, idx) in sampleRecords" :key="idx" class="preview-block__sample">
                <span class="preview-block__sample-label">{{ rec.label }}</span>
                <span class="preview-block__sample-value">{{ rec.value }}</span>
              </li>
            </ul>
          </div>

          <div class="aq-form-label" style="margin-top: 16px;">PROGRESS NOTE PREVIEW</div>
          <div class="preview-block__note-row">
            <div class="preview-block__note">{{ preview.sample_note }}</div>
            <i class="el-icon-refresh kr-action-icon" title="Regenerate note" @click="regenerateNote" />
          </div>

          <el-collapse class="preview-block__collapse">
            <el-collapse-item title="View source details">
              <div class="preview-detail-grid">
                <div class="preview-detail-grid__row">
                  <span class="preview-detail-grid__key">Records</span>
                  <span class="preview-detail-grid__val">{{ preview.source_summary.records_counted }}</span>
                </div>
                <div v-if="preview.source_summary.date_range" class="preview-detail-grid__row">
                  <span class="preview-detail-grid__key">Date range</span>
                  <span class="preview-detail-grid__val">
                    {{ preview.source_summary.date_range.start }} – {{ preview.source_summary.date_range.end }}
                  </span>
                </div>
                <div v-if="preview.source_summary.filters_applied && preview.source_summary.filters_applied.length" class="preview-detail-grid__row">
                  <span class="preview-detail-grid__key">Filters</span>
                  <span class="preview-detail-grid__val">{{ preview.source_summary.filters_applied.join(', ') }}</span>
                </div>
                <div class="preview-detail-grid__row">
                  <span class="preview-detail-grid__key">Action</span>
                  <span class="preview-detail-grid__val preview-detail-grid__mono">{{ recipe.recipe.action_slug }}</span>
                </div>
                <div class="preview-detail-grid__row">
                  <span class="preview-detail-grid__key">Aggregation</span>
                  <span class="preview-detail-grid__val">{{ recipe.recipe.post_processing.aggregation }}<span v-if="recipe.recipe.post_processing.value_field"> on {{ recipe.recipe.post_processing.value_field }}</span></span>
                </div>
              </div>
            </el-collapse-item>
          </el-collapse>

          <div v-if="preview.warnings && preview.warnings.length" class="preview-block__warnings">
            <button
              v-for="(w, idx) in preview.warnings"
              :key="idx"
              type="button"
              class="preview-warning preview-warning--button"
              @click="diagnosticVisible = true"
            >
              <i class="el-icon-warning-outline" />
              <span class="preview-warning__text">{{ w }}</span>
              <span class="preview-warning__cta">Diagnose</span>
            </button>
          </div>
        </div>
        <div v-else class="preview-empty">
          Click “Test now” to fetch the live result from {{ displayName }}.
        </div>
      </section>

      <!-- 5. CADENCE -->
      <section v-if="recipe && hasActiveConnection && preview" class="dialog-section">
        <AutomationCadencePicker v-model="cadence" />
      </section>

      <!-- 6. NOTIFICATIONS -->
      <section v-if="recipe && hasActiveConnection && preview" class="dialog-section">
        <div class="aq-form-label">WHO GETS NOTIFIED</div>
        <div class="notification-checks">
          <el-checkbox v-model="notifyKrOwners">KR owners</el-checkbox>
          <el-checkbox v-model="notifyGoalOwner">Goal owner</el-checkbox>
          <el-checkbox v-model="notifySpecificPeople">Specific people</el-checkbox>
        </div>
        <div v-if="notifySpecificPeople" class="notification-people">
          <AutomationRecipientPicker v-model="specificUserIds" :team-id="teamId" />
        </div>
        <div class="notification-fineprint">
          Anyone with access to this Key Result can see updates in history.
        </div>
      </section>
    </div>

    <div slot="footer" class="kr-automation-dialog__footer">
      <el-button v-if="recipe && hasActiveConnection" size="small" @click="onTest">Test now</el-button>
      <button type="button" class="kr-automation-dialog__manage-link" @click="onManageConnections">
        <happily-icon name="settings" :size="12" />
        <span>Manage connected apps</span>
      </button>
      <div class="kr-automation-dialog__footer-spacer" />
      <el-button size="small" @click="handleClose">Cancel</el-button>
      <el-button
        type="primary"
        size="small"
        :disabled="!canSave || saving"
        :loading="saving"
        @click="onSave"
      >
        Save automation
      </el-button>
    </div>
    <AutomationToolkitBrowser
      :visible="browserVisible"
      @close="browserVisible = false"
      @pick="onPickFromBrowser"
    />
    <AutomationDiagnosticDialog
      :visible="diagnosticVisible"
      :recipe="diagnosticRecipe"
      :preview="preview"
      :toolkit-slug="suggestedToolkitSlug"
      :toolkit-name="displayName"
      :toolkit-logo-url="activeToolkitLogo"
      :unit="krUnit"
      @update:visible="diagnosticVisible = $event"
      @close="diagnosticVisible = false"
      @edit-prompt="onDiagnosticEditPrompt"
      @switch-source="onDiagnosticSwitchSource"
      @reconnect="onDiagnosticReconnect"
    />
  </el-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import AutomationCadencePicker from './AutomationCadencePicker.vue'
import AutomationRecipientPicker from './AutomationRecipientPicker.vue'
import AutomationToolkitMark from './AutomationToolkitMark.vue'
import AutomationRecipeFlow from './AutomationRecipeFlow.vue'
import AutomationToolkitBrowser from './AutomationToolkitBrowser.vue'
import AutomationDiagnosticDialog from './AutomationDiagnosticDialog.vue'
import {
  isKrAutomationDemoMode,
  completeDemoConnection,
} from '@/services/metricSourceService'
import {
  detectToolkitFromPrompt,
  simpleIconUrl,
} from '@/services/krAutomationDemoBackend'
import { formatRecipeParams, detectPromptRecipeDrift } from '@/services/recipeParams'

const PLACEHOLDER = 'e.g. From HubSpot, sum the value of every closed-won deal in the past 30 days in THB.'

const TOOLKIT_DISPLAY_NAMES = {
  hubspot: 'HubSpot',
  salesforce: 'Salesforce',
  stripe: 'Stripe',
  linear: 'Linear',
  notion: 'Notion',
  github: 'GitHub',
  slack: 'Slack',
  zendesk: 'Zendesk',
  intercom: 'Intercom',
  mixpanel: 'Mixpanel',
  google_analytics: 'Google Analytics',
  google_sheets: 'Google Sheets',
  jira: 'Jira',
}

const EXAMPLE_PROMPTS = [
  {
    label: 'HubSpot · closed-won deals',
    text: 'From HubSpot, sum the value of every closed-won deal in the past 30 days in THB.',
    toolkit: 'hubspot',
  },
  {
    label: 'Linear · issues completed',
    text: 'From Linear, count the issues the Platform team completed this quarter.',
    toolkit: 'linear',
  },
  {
    label: 'Stripe · MRR',
    text: 'From Stripe, sum monthly recurring revenue across active subscriptions this month.',
    toolkit: 'stripe',
  },
  {
    label: 'GitHub · PRs merged',
    text: 'From GitHub, count pull requests merged into main this month.',
    toolkit: 'github',
  },
]

// Toolkit-specific starter recipes shown once the user has signaled which app
// they want to pull from (picked a tile, picked from Browse, or typed a name
// the live detector recognized). Clicking a pill replaces the prompt with a
// full template the user can then tweak; same applyExample path as the generic
// EXAMPLE_PROMPTS row above. Keep these short, action-oriented, and
// time-bounded — the model interprets them better with a window built in.
const RECIPES_BY_TOOLKIT = {
  happily: [
    { label: 'Manager reply rate', text: 'From Happily, average manager reply rate across the company this quarter.' },
    { label: 'Engagement (DEBI)', text: 'From Happily, DEBI score for the company this quarter.' },
    { label: 'eNPS', text: 'From Happily, eNPS score for the company this quarter.' },
    { label: 'Recognitions given', text: 'From Happily, count of recognitions given across the company this quarter.' },
    { label: 'Turnover rate', text: 'From Happily, turnover rate across the company this quarter.' },
    { label: 'Participation', text: 'From Happily, pulse participation rate across the company this month.' },
  ],
  hubspot: [
    { label: 'Closed-won deals', text: 'From HubSpot, sum the value of every closed-won deal in the past 30 days in THB.' },
    { label: 'New deals', text: 'From HubSpot, count of deals created this quarter.' },
    { label: 'Pipeline value', text: 'From HubSpot, sum the value of all open deals in the pipeline.' },
    { label: 'Win rate', text: 'From HubSpot, win rate across deals closed in the past 90 days.' },
  ],
  stripe: [
    { label: 'MRR', text: 'From Stripe, sum monthly recurring revenue across active subscriptions this month.' },
    { label: 'Active subscriptions', text: 'From Stripe, count of active subscriptions this month.' },
    { label: 'Revenue', text: 'From Stripe, sum revenue collected in the past 30 days.' },
    { label: 'New customers', text: 'From Stripe, count of new paying customers this quarter.' },
  ],
  linear: [
    { label: 'Issues completed', text: 'From Linear, count issues completed by the Platform team this quarter.' },
    { label: 'Bugs fixed', text: 'From Linear, count of bugs marked done in the past 30 days.' },
    { label: 'Cycle time', text: 'From Linear, average cycle time across completed issues this month.' },
  ],
  github: [
    { label: 'PRs merged', text: 'From GitHub, count pull requests merged into main this month.' },
    { label: 'Issues closed', text: 'From GitHub, count of issues closed this quarter.' },
    { label: 'Commits to main', text: 'From GitHub, count of commits pushed to main this week.' },
  ],
  salesforce: [
    { label: 'Closed-won', text: 'From Salesforce, sum the value of closed-won opportunities in the past 30 days.' },
    { label: 'Pipeline', text: 'From Salesforce, sum pipeline value of all open opportunities.' },
    { label: 'New leads', text: 'From Salesforce, count of leads created this month.' },
  ],
  google_analytics: [
    { label: 'Active users', text: 'From Google Analytics, count active users in the past 30 days.' },
    { label: 'Page views', text: 'From Google Analytics, sum page views across the site this month.' },
    { label: 'Conversions', text: 'From Google Analytics, count of completed conversions this quarter.' },
  ],
  google_sheets: [
    { label: 'Column total', text: 'From Google Sheets, sum the values in column A of the dashboard sheet this month.' },
    { label: 'Row count', text: 'From Google Sheets, count rows added to the leads sheet this week.' },
  ],
  notion: [
    { label: 'Tasks completed', text: 'From Notion, count tasks marked Done in the Q1 Tasks database this quarter.' },
    { label: 'Pages created', text: 'From Notion, count pages created in the Knowledge base this month.' },
  ],
  jira: [
    { label: 'Tickets resolved', text: 'From Jira, count of issues moved to Done in the current sprint.' },
    { label: 'Story points', text: 'From Jira, sum of story points completed this quarter.' },
  ],
  zendesk: [
    { label: 'Tickets resolved', text: 'From Zendesk, count of tickets marked solved in the past 30 days.' },
    { label: 'CSAT score', text: 'From Zendesk, average CSAT score across resolved tickets this month.' },
  ],
  mixpanel: [
    { label: 'Active users', text: 'From Mixpanel, count of unique users with any event in the past 7 days.' },
    { label: 'Funnel conversion', text: 'From Mixpanel, conversion rate from Signup to Activated this month.' },
  ],
  intercom: [
    { label: 'Resolved conversations', text: 'From Intercom, count of conversations marked resolved this week.' },
  ],
  slack: [
    { label: 'Channel activity', text: 'From Slack, count of messages posted to #wins this week.' },
  ],
}

const tk = (slug, name) => ({ slug, name, logo_url: simpleIconUrl(slug) })
const TOOLKIT_SHORTCUTS = [
  tk('hubspot', 'HubSpot'),
  tk('linear', 'Linear'),
  tk('stripe', 'Stripe'),
  tk('github', 'GitHub'),
  tk('google_analytics', 'Google Analytics'),
  tk('google_sheets', 'Google Sheets'),
]

// Toolkit name → slug. The mirror layer highlights any of these tokens with
// an official-looking pill (logo + label). Includes 'Happily' so the
// company's own data source is treated identically to third-party connectors.
// Order matters: regex alternation is greedy left-to-right, so multi-word
// tokens MUST come first ("Google Analytics" wins over "Google").
const TOKEN_TO_SLUG = {
  'Google Analytics': 'google_analytics',
  'Google Sheets': 'google_sheets',
  'HubSpot': 'hubspot',
  'Salesforce': 'salesforce',
  'Stripe': 'stripe',
  'Linear': 'linear',
  'Notion': 'notion',
  'GitHub': 'github',
  'Slack': 'slack',
  'Zendesk': 'zendesk',
  'Intercom': 'intercom',
  'Mixpanel': 'mixpanel',
  'Jira': 'jira',
  'Happily': 'happily',
}
const TOOLKIT_NAME_TOKENS = Object.keys(TOKEN_TO_SLUG).sort((a, b) => b.length - a.length)
const TOOLKIT_NAME_RE = new RegExp(
  '\\b(' + TOOLKIT_NAME_TOKENS.map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') + ')\\b',
  'gi'
)
// Lowercased lookup so case-insensitive matches resolve to the canonical slug.
const TOKEN_TO_SLUG_LC = Object.keys(TOKEN_TO_SLUG).reduce((acc, k) => {
  acc[k.toLowerCase()] = TOKEN_TO_SLUG[k]
  return acc
}, {})

const escapeHtml = (s) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

export default {
  name: 'KeyResultAutomationDialog',
  components: {
    AutomationCadencePicker,
    AutomationRecipientPicker,
    AutomationToolkitMark,
    AutomationRecipeFlow,
    AutomationToolkitBrowser,
    AutomationDiagnosticDialog,
  },
  props: {
    visible: { type: Boolean, default: false },
    keyResult: { type: Object, default: null },
    goalId: { type: String, default: '' },
    teamId: { type: String, default: '' },
    goalOwnerEmail: { type: String, default: '' },
  },
  data() {
    return {
      prompt: '',
      placeholder: PLACEHOLDER,
      cadence: this.defaultCadence(),
      notifyKrOwners: true,
      notifyGoalOwner: true,
      notifySpecificPeople: false,
      specificUserIds: [],
      connecting: false,
      activeConnectionIndex: 0,
      saving: false,
      lifecycleBusy: false,
      autoPreviewTimer: null,
      toolkitShortcuts: TOOLKIT_SHORTCUTS,
      totalToolkitCount: 250,
      demoMode: isKrAutomationDemoMode(),
      browserVisible: false,
      // When the user picks a tile from the Browse modal, capture the slug so
      // we can pass it as toolkit_hint on the next Interpret call. This makes
      // the routing deterministic (especially important for Happily, where the
      // catalog only loads when toolkit_hint='happily' arrives at the backend).
      // Cleared once Interpret runs so subsequent prompt edits can reroute.
      pickedToolkitSlug: '',
      diagnosticVisible: false,
    }
  },
  computed: {
    ...mapGetters('goals', ['composerState']),
    dialogVisible: {
      get() { return this.visible },
      set(v) { if (!v) this.$emit('close') },
    },
    composing() { return this.composerState.isComposing },
    previewing() { return this.composerState.isPreviewing },
    recipeResponse() { return this.composerState.activeRecipe },
    recipe() {
      const response = this.recipeResponse
      return response && response.recipe && response.recipe.action_slug ? response : null
    },
    interpretationIssue() {
      const response = this.recipeResponse
      if (!response || (response.recipe && response.recipe.action_slug)) return ''
      const questions = Array.isArray(response.needs_clarification) ? response.needs_clarification : []
      return questions[0] || 'This request could not be matched to a supported source action yet.'
    },
    preview() { return this.composerState.activePreview },
    suggestedToolkitSlug() {
      const response = this.recipe || this.recipeResponse
      return response ? response.suggested_toolkit_slug : ''
    },
    displayName() {
      const slug = this.suggestedToolkitSlug || ''
      const response = this.recipe || this.recipeResponse
      const name = response && response.suggested_toolkit_name
      if (name) return name
      if (TOOLKIT_DISPLAY_NAMES[slug]) return TOOLKIT_DISPLAY_NAMES[slug]
      if (slug) return slug.charAt(0).toUpperCase() + slug.slice(1)
      return 'Source'
    },
    krUnit() { return this.keyResult ? (this.keyResult.unit || '') : '' },
    isHappilySource() {
      return this.suggestedToolkitSlug === 'happily'
    },
    connectionsForToolkit() {
      if (!this.suggestedToolkitSlug || this.isHappilySource) return []
      return this.$store.getters['goals/activeConnectionsFor'](this.suggestedToolkitSlug)
    },
    isToolkitConnected() {
      // Returns a function so the template can call `isToolkitConnected(slug)`.
      // Happily is first-party — always treat it as "available" so the tile
      // mark stays consistent with the connection block's behaviour.
      const getter = this.$store.getters['goals/hasActiveConnection']
      return (slug) => slug === 'happily' || getter(slug)
    },
    hasActiveConnection() {
      // Happily-native sources don't need a connected account: the company
      // is the implicit account. Treat them as always-connected so the
      // preview / save flow proceeds without an OAuth step.
      if (this.isHappilySource) return true
      return this.connectionsForToolkit.length > 0
    },
    activeConnection() {
      const list = this.connectionsForToolkit
      if (list.length === 0) return null
      return list[Math.min(this.activeConnectionIndex, list.length - 1)]
    },
    connectCopy() {
      return `${this.displayName} is required to read source data.`
    },
    canSave() {
      if (!this.recipe || !this.preview) return false
      // Happily-native sources don't need a connected account.
      if (this.isHappilySource) return true
      return !!this.activeConnection
    },
    existingMetricSource() {
      return (this.keyResult && this.keyResult.metric_source) || null
    },
    automationPaused() {
      return !!this.existingMetricSource && this.existingMetricSource.enabled === false
    },
    happilyLogoUrl() {
      // Same gold-smiley asset the Browse modal uses for the Happily tile.
      return require('@/assets/images/logo_symbol_only.png')
    },
    highlightedPromptHtml() {
      // Render the prompt with a soft pill background behind any detected
      // toolkit name. Mirror text is transparent; only the highlight shows
      // through, behind the visible textarea glyphs above. The icon-on-pill
      // visual lives on the official "Detected:" pill at the top of this
      // section — keeping the inline mark logo-free preserves perfect
      // character alignment with the textarea (any inline width here would
      // skew subsequent characters out of sync).
      const safe = escapeHtml(this.prompt || '')
      const withTokens = safe.replace(TOOLKIT_NAME_RE, (match) => {
        const slug = TOKEN_TO_SLUG_LC[match.toLowerCase()] || ''
        return `<span class="prompt-mirror__token" data-toolkit="${slug}">${match}</span>`
      })
      // Append a trailing newline so the mirror grows with a final empty line, matching
      // textarea behaviour.
      return withTokens + '\n'
    },
    sampleRecordsToolkit() {
      return this.suggestedToolkitSlug || ''
    },
    sampleRecords() {
      if (!this.preview || !this.preview.source_summary) return []
      return this.preview.source_summary.sample_records || []
    },
    ownerBreakdown() {
      if (!this.preview || !Array.isArray(this.preview.owner_breakdown)) return null
      return this.preview.owner_breakdown
    },
    /**
     * Surfaces the Diagnose CTA when the preview returned something worth
     * inspecting: zero matched records, source warnings, owners the
     * provider couldn't resolve, OR an aggregation mismatch (records came
     * back but the value summed to 0 because the value_field path didn't
     * match the response shape).
     */
    hasPreviewIssue() {
      if (!this.preview) return false
      const summary = this.preview.source_summary || {}
      const records = typeof summary.records_counted === 'number' ? summary.records_counted : null
      if (records === 0) return true
      if (this.hasAggregationMismatch) return true
      if (this.preview.warnings && this.preview.warnings.length) return true
      if (this.preview.unmatched_owners && this.preview.unmatched_owners.length) return true
      return false
    },
    /**
     * Records came back but the aggregation produced 0 — the value_field
     * in post_processing almost certainly doesn't match the response
     * shape (e.g. recipe asked GA4 for "activeUsers" but action_args
     * requested "sessions"). Drives a more prominent inline callout
     * because the small Diagnose pill is easy to miss when the value
     * itself looks "real".
     */
    hasAggregationMismatch() {
      if (!this.preview) return false
      const summary = this.preview.source_summary || {}
      const records = typeof summary.records_counted === 'number' ? summary.records_counted : 0
      if (records <= 0) return false
      const v = this.preview.value
      return v == null || v === 0
    },
    diagnosticRecipe() {
      return this.recipe && this.recipe.recipe ? this.recipe.recipe : null
    },
    /**
     * Human-readable rows ([{ key, value }]) describing what the recipe
     * is actually configured to fetch (metric, property, filter, date
     * range, aggregation). Surfaced in the interpretation card so users
     * spot LLM translation drift before saving.
     */
    recipeParamRows() {
      if (!this.diagnosticRecipe) return []
      return formatRecipeParams(this.diagnosticRecipe, this.suggestedToolkitSlug)
    },
    /**
     * High-confidence heuristic that the LLM picked the wrong metric.
     * If the prompt mentions e.g. "sessions" but action_args.metrics
     * names something different, flag it so the user can re-prompt
     * before wasting time on a preview.
     */
    promptRecipeDrift() {
      return detectPromptRecipeDrift(this.prompt, this.diagnosticRecipe, this.suggestedToolkitSlug)
    },
    targetMeter() {
      if (!this.preview || typeof this.preview.value !== 'number') return null
      const target = this.keyResult && typeof this.keyResult.target_value === 'number'
        ? this.keyResult.target_value
        : null
      if (!target || target <= 0) return null
      const pct = Math.max(0, Math.min(100, Math.round((this.preview.value / target) * 100)))
      return { percent: pct, target }
    },
    detectedToolkit() {
      // Live detection from the textarea content. Falls back to interpreted recipe
      // once the user clicks Interpret. Returns { slug, name, logo_url } or null.
      const response = this.recipe || this.recipeResponse
      if (response && response.suggested_toolkit_slug) {
        return {
          slug: response.suggested_toolkit_slug,
          name: response.suggested_toolkit_name || this.displayName,
          logo_url: simpleIconUrl(response.suggested_toolkit_slug),
        }
      }
      const detected = detectToolkitFromPrompt(this.prompt)
      if (!detected) return null
      return {
        slug: detected.slug,
        name: TOOLKIT_DISPLAY_NAMES[detected.slug] || detected.name,
        logo_url: simpleIconUrl(detected.slug),
      }
    },
    activeToolkitSlug() {
      // Priority: interpreted recipe > picked-from-tile (or Browse) > live-detected.
      // Drives both the recipe-pill row and the routing hint passed to compose.
      if (this.recipeResponse && this.recipeResponse.suggested_toolkit_slug) {
        return this.recipeResponse.suggested_toolkit_slug
      }
      if (this.pickedToolkitSlug) return this.pickedToolkitSlug
      const detected = detectToolkitFromPrompt(this.prompt)
      return detected ? detected.slug : ''
    },
    activeRecipePills() {
      // Toolkit-specific recipes when we know which app the user is targeting,
      // generic mixed examples otherwise. Each entry carries `toolkit` so
      // applyExample can pin pickedToolkitSlug for deterministic routing.
      const slug = this.activeToolkitSlug
      if (slug && RECIPES_BY_TOOLKIT[slug]) {
        const name = TOOLKIT_DISPLAY_NAMES[slug] || (slug.charAt(0).toUpperCase() + slug.slice(1))
        return {
          recipes: RECIPES_BY_TOOLKIT[slug].map((r) => ({ ...r, toolkit: slug })),
          label: `${name} recipes`,
          isToolkitSpecific: true,
        }
      }
      return { recipes: EXAMPLE_PROMPTS, label: 'Try', isToolkitSpecific: false }
    },
    showRecipePills() {
      // Hide once the user interprets — the pill row is a starting-point aid,
      // not navigation. Keep showing toolkit-specific pills even mid-typing
      // (so users can swap recipes after picking a tile); generic pills only
      // when the textarea is empty so they don't tempt overwrites.
      if (this.recipe) return false
      if (this.activeRecipePills.isToolkitSpecific) return true
      return !this.prompt
    },
    activeToolkitLogo() {
      const slug = this.suggestedToolkitSlug
      return slug ? simpleIconUrl(slug) : ''
    },
  },
  watch: {
    visible(open) {
      if (open) {
        this.resetForExistingMetricSource()
        // Fetch every connection in one go so the toolkit-shortcut tiles
        // can show a "connected" mark for apps already wired up. The global
        // state then powers `isToolkitConnected(slug)` without needing a
        // per-slug subscription for every tile we render.
        this.$store.dispatch('goals/listConnectedAccounts', {})
      } else {
        this.cleanup()
      }
    },
    suggestedToolkitSlug(slug, prevSlug) {
      if (slug && slug !== prevSlug) {
        this.$store.dispatch('goals/subscribeConnectedAccounts', { toolkitSlug: slug })
        this.$store.dispatch('goals/listConnectedAccounts', { toolkitSlug: slug })
      }
    },
    hasActiveConnection(active) {
      if (active && this.recipe && !this.preview && !this.previewing) {
        this.runPreview()
      }
    },
  },
  beforeDestroy() {
    this.cleanup()
  },
  methods: {
    defaultCadence() {
      return {
        frequency: 'weekly',
        day: 'monday',
        time: '09:00',
        suppress_if_no_meaningful_change: true,
      }
    },
    krContext() {
      // Drives Happily auto-fill (owner_email, owner_emails, team_id) and
      // composer mode-aware behavior (mode). company_id is resolved
      // server-side from auth.ctx, not threaded through here.
      const kr = this.keyResult || {}
      const ownerEmails = Array.isArray(kr.owner_emails) ? kr.owner_emails.filter(Boolean) : []
      const ownerEmail = ownerEmails[0] || this.goalOwnerEmail || ''
      return {
        title: kr.title || '',
        unit: kr.unit || '',
        target_value: kr.target_value,
        start_value: kr.start_value,
        current_value: kr.current_value,
        owner_email: ownerEmail,
        owner_emails: ownerEmails,
        team_id: this.teamId || '',
        mode: kr.mode || 'collaborative',
      }
    },
    async onInterpret() {
      const result = await this.$store.dispatch('goals/composeMetricRecipe', {
        krId: this.keyResult ? this.keyResult.kr_id : '',
        prompt: this.prompt,
        krContext: this.krContext(),
        toolkitHint: this.pickedToolkitSlug || undefined,
      })
      if (!result.success) {
        this.$message.error('Could not interpret prompt. Please try again.')
        return
      }
      // Reset stale preview when re-interpreting.
      this.$store.commit('goals/setComposerPreview', null)
      const slug = result.recipe && result.recipe.suggested_toolkit_slug
      // Happily-native sources don't have connected_accounts; skip the
      // subscribe/list to avoid a no-op Firestore listener.
      if (slug && slug !== 'happily') {
        this.$store.dispatch('goals/subscribeConnectedAccounts', { toolkitSlug: slug })
        this.$store.dispatch('goals/listConnectedAccounts', { toolkitSlug: slug })
      }
      // For Happily, immediately auto-run the preview since there's no
      // OAuth gate to wait on. Saves the user a click.
      if (slug === 'happily') {
        this.$nextTick(() => this.runPreview())
      }
    },
    onClarificationChip(question) {
      const sep = this.prompt.endsWith('\n') ? '' : '\n\n'
      this.prompt = `${this.prompt}${sep}${question}: `
      this.$nextTick(() => {
        const ref = this.$refs.promptInput
        if (ref && ref.focus) ref.focus()
      })
    },
    applyExample(example) {
      this.prompt = example.text
      // Pin the toolkit so Interpret routes deterministically (especially
      // critical for Happily, where the catalog only loads on toolkit_hint).
      if (example && example.toolkit) this.pickedToolkitSlug = example.toolkit
      this.$nextTick(() => {
        const ref = this.$refs.promptInput
        if (ref && ref.focus) ref.focus()
      })
    },
    onPickFromBrowser(toolkit) {
      this.insertToolkit(toolkit)
    },
    onDiagnosticEditPrompt() {
      this.$nextTick(() => {
        const ref = this.$refs.promptInput
        if (ref && ref.focus) ref.focus()
      })
    },
    onDiagnosticSwitchSource() {
      this.browserVisible = true
    },
    onDiagnosticReconnect() {
      this.$emit('manage-connections')
    },
    insertToolkit(toolkit) {
      const stub = `From ${toolkit.name}, `
      // If the prompt already starts with "From <something>, ", swap it. Otherwise prepend.
      if (/^from\s+[^,]+,\s*/i.test(this.prompt)) {
        this.prompt = this.prompt.replace(/^from\s+[^,]+,\s*/i, stub)
      } else if (this.prompt.trim()) {
        this.prompt = `${stub}${this.prompt.trim()}`
      } else {
        this.prompt = stub
      }
      this.pickedToolkitSlug = (toolkit && toolkit.slug) || ''
      this.$nextTick(() => {
        const ref = this.$refs.promptInput
        if (ref && ref.focus) ref.focus()
      })
    },
    async onConnect() {
      if (!this.suggestedToolkitSlug) return
      this.connecting = true
      try {
        if (this.demoMode) {
          // Skip the new-tab OAuth round trip; complete the connection in-place
          // and refresh the connected-accounts list so the watcher fires.
          await completeDemoConnection(this.suggestedToolkitSlug)
          await this.$store.dispatch('goals/listConnectedAccounts', {
            toolkitSlug: this.suggestedToolkitSlug,
          })
          return
        }
        const result = await this.$store.dispatch('goals/startConnectAccount', {
          toolkitSlug: this.suggestedToolkitSlug,
        })
        if (result.success && result.redirect_url) {
          window.open(result.redirect_url, '_blank')
        } else {
          this.$message.error('Could not start connection.')
        }
      } finally {
        // keep `connecting` true while waiting for snapshot to fire — the watcher resets it.
      }
    },
    cycleConnection() {
      const list = this.connectionsForToolkit
      if (list.length === 0) return
      this.activeConnectionIndex = (this.activeConnectionIndex + 1) % list.length
    },
    async runPreview() {
      if (!this.recipe) return
      if (!this.isHappilySource && !this.activeConnection) return
      await this.$store.dispatch('goals/previewMetricRecipe', {
        recipe: this.recipe.recipe,
        connectedAccountId: this.isHappilySource ? '' : this.activeConnection.id,
        toolkitSlug: this.isHappilySource ? 'happily' : undefined,
        krContext: this.krContext(),
      })
      this.connecting = false
    },
    async regenerateNote() {
      if (!this.recipe) return
      if (!this.isHappilySource && !this.activeConnection) return
      await this.$store.dispatch('goals/previewMetricRecipe', {
        recipe: this.recipe.recipe,
        connectedAccountId: this.isHappilySource ? '' : this.activeConnection.id,
        toolkitSlug: this.isHappilySource ? 'happily' : undefined,
        krContext: this.krContext(),
        regenerateNote: true,
      })
    },
    async onTest() {
      await this.runPreview()
    },
    buildMetricSourcePayload() {
      return {
        enabled: true,
        toolkit_slug: this.suggestedToolkitSlug,
        connected_account_id: this.isHappilySource ? '' : this.activeConnection.id,
        original_prompt: this.prompt,
        compiled_recipe: this.recipe.recipe,
        cadence: { ...this.cadence },
        notification_policy: {
          enabled: true,
          notify_on: 'published_update',
          recipients: {
            kr_owners: this.notifyKrOwners,
            goal_owner: this.notifyGoalOwner,
            specific_user_ids: this.notifySpecificPeople ? [...this.specificUserIds] : [],
            manager_chain: false,
          },
        },
      }
    },
    existingMetricSourcePayload(enabled) {
      const source = this.existingMetricSource || {}
      return {
        enabled,
        toolkit_slug: source.toolkit_slug,
        connected_account_id: source.connected_account_id || '',
        original_prompt: source.original_prompt || '',
        compiled_recipe: source.compiled_recipe,
        cadence: source.cadence,
        notification_policy: source.notification_policy,
      }
    },
    async onToggleAutomation() {
      if (!this.existingMetricSource || this.lifecycleBusy) return
      const enabled = this.automationPaused
      this.lifecycleBusy = true
      try {
        const result = await this.$store.dispatch('goals/saveKrMetricSource', {
          teamId: this.teamId,
          goalId: this.goalId,
          krId: this.keyResult.kr_id,
          metricSource: this.existingMetricSourcePayload(enabled),
        })
        if (!result.success) {
          this.$message.error(this.$t('performance.goals.toast_automation_update_failed'))
          return
        }
        this.$message.success(this.$t(enabled
          ? 'performance.goals.toast_automation_resumed'
          : 'performance.goals.toast_automation_paused'))
        this.$emit('saved', {
          krId: this.keyResult.kr_id,
          metricSource: this.existingMetricSourcePayload(enabled),
        })
        this.handleClose()
      } finally {
        this.lifecycleBusy = false
      }
    },
    async onDeleteAutomation() {
      if (!this.existingMetricSource || this.lifecycleBusy) return
      try {
        await this.$confirm(
          this.$t('performance.goals.toast_confirm_delete_automation'),
          this.$t('performance.goals.toast_delete_automation_title'),
          { confirmButtonText: 'Delete', cancelButtonText: 'Cancel', type: 'warning' },
        )
      } catch (error) {
        return
      }

      this.lifecycleBusy = true
      try {
        const pauseResult = await this.$store.dispatch('goals/saveKrMetricSource', {
          teamId: this.teamId,
          goalId: this.goalId,
          krId: this.keyResult.kr_id,
          metricSource: this.existingMetricSourcePayload(false),
        })
        if (!pauseResult.success) {
          this.$message.error(this.$t('performance.goals.toast_automation_delete_failed'))
          return
        }
        const deleteResult = await this.$store.dispatch('goals/updateKeyResultFields', {
          teamId: this.teamId,
          goalId: this.goalId,
          krId: this.keyResult.kr_id,
          fields: { metric_source: null },
        })
        if (!deleteResult.success) {
          this.$message.error(this.$t('performance.goals.toast_automation_delete_failed'))
          return
        }
        this.$message.success(this.$t('performance.goals.toast_automation_deleted'))
        this.$emit('deleted', { krId: this.keyResult.kr_id })
        this.handleClose()
      } finally {
        this.lifecycleBusy = false
      }
    },
    cadenceSummary() {
      const c = this.cadence
      const time = c.time || '09:00'
      if (c.frequency === 'daily') return `daily at ${time}`
      if (c.frequency === 'monthly') return `monthly on day ${c.day_of_month || 1} at ${time}`
      const day = (c.day || 'monday')
      return `weekly on ${day.charAt(0).toUpperCase() + day.slice(1)} at ${time}`
    },
    async onSave() {
      if (!this.canSave) return
      this.saving = true
      try {
        const payload = this.buildMetricSourcePayload()
        const result = await this.$store.dispatch('goals/saveKrMetricSource', {
          teamId: this.teamId,
          goalId: this.goalId,
          krId: this.keyResult.kr_id,
          metricSource: payload,
        })
        if (result.success) {
          const sync = result.first_sync || null
          if (sync && sync.status === 'success') {
            this.$message.success(`Automation saved and KR updated. Future updates will run ${this.cadenceSummary()}.`)
          } else if (sync && sync.status === 'no_change') {
            this.$message.success(`Automation saved. The KR already matches the latest source value.`)
          } else if (sync && sync.status === 'zero_records') {
            this.$message.success(`Automation saved and KR updated from the source. Future updates will run ${this.cadenceSummary()}.`)
          } else if (sync && sync.status === 'auth_expired') {
            this.$message.warning('Automation saved, but the source needs to be reconnected before it can update this KR.')
          } else if (sync && sync.status === 'error') {
            this.$message.warning('Automation saved, but the first update could not run. Check the automation activity for details.')
          } else {
            this.$message.success(`Automation saved. Future updates will run ${this.cadenceSummary()}.`)
          }
          this.$emit('saved', { krId: this.keyResult.kr_id, metricSource: payload })
          this.handleClose()
        } else {
          this.$message.error('Could not save automation.')
        }
      } finally {
        this.saving = false
      }
    },
    handleClose() {
      this.$emit('close')
    },
    onManageConnections() {
      this.$emit('manage-connections')
    },
    cleanup() {
      this.$store.dispatch('goals/resetMetricComposer')
      if (this.suggestedToolkitSlug) {
        this.$store.dispatch('goals/unsubscribeConnectedAccounts', { toolkitSlug: this.suggestedToolkitSlug })
      }
      this.prompt = ''
      this.cadence = this.defaultCadence()
      this.notifyKrOwners = true
      this.notifyGoalOwner = true
      this.notifySpecificPeople = false
      this.specificUserIds = []
      this.connecting = false
      this.activeConnectionIndex = 0
      this.lifecycleBusy = false
    },
    resetForExistingMetricSource() {
      const ms = this.keyResult ? this.keyResult.metric_source : null
      if (!ms) return
      this.prompt = ms.original_prompt || ''
      this.cadence = { ...this.defaultCadence(), ...(ms.cadence || {}) }
      const recipients = ms.notification_policy ? ms.notification_policy.recipients : null
      if (recipients) {
        this.notifyKrOwners = !!recipients.kr_owners
        this.notifyGoalOwner = !!recipients.goal_owner
        this.notifySpecificPeople = !!(recipients.specific_user_ids && recipients.specific_user_ids.length)
        this.specificUserIds = recipients.specific_user_ids ? [...recipients.specific_user_ids] : []
      }
    },
    formatValue(v) {
      if (v === null || v === undefined) return 'n/a'
      if (typeof v !== 'number') return String(v)
      if (Math.abs(v) >= 1e6) return (v / 1e6).toFixed(2) + 'M'
      if (Math.abs(v) >= 1e3) return (v / 1e3).toFixed(1) + 'K'
      return Number.isInteger(v) ? String(v) : v.toFixed(2)
    },
  },
}
</script>

<style>
/* Global selector for el-dialog custom-class */
.kr-automation-dialog .el-dialog__header {
  padding: 24px 32px 8px 32px;
}
.kr-automation-dialog .el-dialog__body {
  padding: 12px 32px 24px 32px;
}
.kr-automation-dialog .el-dialog__footer {
  padding: 16px 32px 24px 32px;
}
</style>

<style scoped>
.kr-automation-dialog__header {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.kr-automation-dialog__eyebrow-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.kr-automation-dialog__demo-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fff8ec;
  color: #b67200;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  border: 1px solid #f4d893;
  cursor: help;
}

.kr-automation-dialog__kr-title {
  font-size: 13px;
  font-weight: 500;
  color: #4a4a4a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kr-automation-dialog__heading {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin-top: 4px;
}

.kr-automation-dialog__body {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.automation-management-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 14px;
  background: #f8f6fc;
  border: 1px solid #e3d9f5;
  border-radius: 10px;
}

.automation-management-bar__status {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.automation-management-bar__dot {
  width: 9px;
  height: 9px;
  flex: 0 0 9px;
  border-radius: 50%;
  background: #3ac47d;
  box-shadow: 0 0 0 3px rgba(58, 196, 125, 0.14);
}

.automation-management-bar__dot--paused {
  background: #909399;
  box-shadow: 0 0 0 3px rgba(144, 147, 153, 0.14);
}

.automation-management-bar__copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.automation-management-bar__label {
  color: #2d2140;
  font-size: 13px;
  font-weight: 700;
}

.automation-management-bar__hint {
  color: #77717f;
  font-size: 11px;
  line-height: 1.35;
}

.automation-management-bar__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

@media (max-width: 700px) {
  .automation-management-bar {
    align-items: flex-start;
    flex-direction: column;
  }
}

.dialog-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dialog-section__cta {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}

/* `.aq-form-label` is global — defined in element-variables.scss:813. Don't
   redeclare it here; the scoped clone wouldn't be inherited by the browser
   subdialog, and the spec already lives in one place. */

/* Mirror-layer syntax highlighting for the prompt textarea. The textarea and the
   mirror share the exact same font, padding, and wrapping rules; both render
   transparent text. The mirror layer adds colored backgrounds behind detected
   toolkit names; the textarea provides the visible glyphs. */
.prompt-input-wrap {
  position: relative;
}

.prompt-mirror {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 5px 15px;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  color: transparent;
  pointer-events: none;
  z-index: 1;
  border: 1px solid transparent;
  border-radius: 4px;
}

/* `.prompt-mirror__token` lives in the global block below — the spans are
   injected via v-html, which strips the scoped data-attribute and would
   otherwise miss any scoped selector. */
</style>
<style>
/* Element UI's textarea sits inside .el-input. We need it to render above the
   mirror, with a transparent background, so the mirror's colored highlights
   show behind the visible glyphs. */
.kr-automation-dialog .prompt-input-wrap .el-textarea__inner {
  background: transparent !important;
  position: relative;
  z-index: 2;
}

/* Whisper-thin recognition mark on detected connector names. Just a brand-
   color dotted underline — no fill, no border, no box. text-decoration is
   drawn separately from the text glyphs, so it renders against the mirror's
   `color: transparent` text and shows through the textarea above (which has
   no decoration of its own at this y-position). `text-underline-offset`
   pushes the dots ~2px down so they clear descenders like the "p" in
   HubSpot. `text-decoration-thickness: 1.5px` gives the dots enough weight
   to read at small sizes without becoming a heavy line. No inline width or
   padding — alignment with the textarea stays byte-perfect. */
.kr-automation-dialog .prompt-mirror__token {
  background: transparent;
  padding: 0;
  margin: 0;
  box-shadow: none;
  text-decoration: underline dotted #6c30cc;
  text-decoration-thickness: 1.5px;
  text-underline-offset: 2px;
}

/* Happily echoes the same dotted-mark pattern in brand gold. */
.kr-automation-dialog .prompt-mirror__token[data-toolkit="happily"] {
  text-decoration-color: #d99800;
}

/* When a toolkit is detected, the textarea picks up a soft brand-tinted
   frame and inner glow — confirms "we've registered your selection" without
   competing with the Element UI focus state. Falls back gracefully when
   focused: brand border + brand focus ring stack cleanly. */
.kr-automation-dialog .prompt-input-wrap--has-toolkit .el-textarea__inner {
  border-color: #d8c6f0 !important;
  box-shadow: 0 0 0 3px rgba(108, 48, 204, 0.06) !important;
  transition: border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease;
}

.kr-automation-dialog .prompt-input-wrap--has-toolkit .el-textarea__inner:focus,
.kr-automation-dialog .prompt-input-wrap--has-toolkit .el-textarea__inner:hover {
  border-color: #6c30cc !important;
  box-shadow: 0 0 0 3px rgba(108, 48, 204, 0.12) !important;
}

/* Brand-color the Element UI primary button and checkbox inside this dialog.
   The app has legacy scoped `.el-button--primary[data-v-...]{ #32c2d5 }` rules
   on goals.vue that bleed into descendant components, and Element UI's default
   checkbox is `#409eff` blue. Both contradict the design-system primary
   `#6c30cc`. `!important` matches the holiday-preset-dialog precedent — needed
   because legacy `[data-v-...]` rules carry the same specificity but can win
   on source-order through the late-loading bundle. */
.kr-automation-dialog .el-button--primary,
.kr-automation-dialog .el-button--primary:hover,
.kr-automation-dialog .el-button--primary:focus,
.kr-automation-dialog .el-button--primary:active {
  background-color: #6c30cc !important;
  border-color: #6c30cc !important;
  color: #ffffff !important;
}
.kr-automation-dialog .el-button--primary:hover,
.kr-automation-dialog .el-button--primary:focus {
  background-color: #522698 !important;
  border-color: #522698 !important;
}
.kr-automation-dialog .el-button--primary.is-disabled,
.kr-automation-dialog .el-button--primary.is-disabled:hover,
.kr-automation-dialog .el-button--primary.is-disabled:focus,
.kr-automation-dialog .el-button--primary.is-disabled:active {
  background-color: #d8c6f0 !important;
  border-color: #d8c6f0 !important;
  color: #ffffff !important;
}

.kr-automation-dialog .el-checkbox__input.is-checked .el-checkbox__inner,
.kr-automation-dialog .el-checkbox__input.is-indeterminate .el-checkbox__inner {
  background-color: #6c30cc !important;
  border-color: #6c30cc !important;
}
.kr-automation-dialog .el-checkbox__input.is-checked + .el-checkbox__label {
  color: #1a1a1a !important;
}
.kr-automation-dialog .el-checkbox__inner:hover {
  border-color: #6c30cc !important;
}
.kr-automation-dialog .el-checkbox__input.is-focus .el-checkbox__inner {
  border-color: #6c30cc !important;
}
</style>
<style scoped>
.prompt-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 18px;
}

/* Official "registered source" pill: toolkit logo + name + a small status
   tag, styled as one cohesive chip. Brand-tinted fill signals an active
   selection (vs. an inert label). This is the canonical logo + label
   surface — the inline mirror highlight is a secondary cue that just marks
   the recognized word in the prompt text. */
.detected-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px 4px 6px;
  border-radius: 999px;
  background: #f7f1ff;
  border: 1px solid #e6dafa;
  box-shadow: 0 1px 2px rgba(108, 48, 204, 0.06);
  color: #1a1a1a;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1;
}

.detected-pill__label {
  color: #1a1a1a;
}

.detected-pill__hint {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #6c30cc;
  padding-left: 8px;
  border-left: 1px solid #e6dafa;
}

.detected-fade-enter-active,
.detected-fade-leave-active {
  transition: opacity 180ms ease, transform 180ms ease;
}
.detected-fade-enter,
.detected-fade-leave-to {
  opacity: 0;
  transform: translateY(-2px);
}

.example-chip-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 4px;
}

.example-chip-row__label {
  margin-right: 2px;
}

.example-chip {
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  color: #4a4a4a;
  background: #fafbfc;
  border: 1px solid #e2e4ea;
  border-radius: 999px;
  padding: 4px 10px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  white-space: nowrap;
}

.example-chip:hover {
  background: #f2ecfb;
  border-color: #d8c6f0;
  color: #6c30cc;
}

/* Toolkit-specific recipe pills get a soft brand tint so they read as a
   curated set rather than the generic "try this" examples. */
.example-chip--toolkit {
  background: #faf6ff;
  border-color: #ebdcff;
  color: #4a4a4a;
}

.example-chip--toolkit:hover {
  background: #f2ecfb;
  border-color: #6c30cc;
  color: #6c30cc;
}

.toolkit-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.toolkit-row__label {
  margin-right: 2px;
}

.toolkit-tile {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  color: #4a4a4a;
  background: #ffffff;
  border: 1px solid #e2e4ea;
  border-radius: 8px;
  padding: 5px 9px 5px 6px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.toolkit-tile:hover {
  border-color: #d8c6f0;
  transform: translateY(-1px);
}

.toolkit-tile--active {
  border-color: #6c30cc;
  color: #6c30cc;
  background: #faf6ff;
}

.toolkit-tile--connected {
  border-color: #c8ecd6;
  background: #f3fbf6;
}
.toolkit-tile--connected:hover {
  border-color: #3ac47d;
}
.toolkit-tile--connected.toolkit-tile--active {
  border-color: #6c30cc;
  background: #faf6ff;
}

.toolkit-tile__label {
  white-space: nowrap;
  letter-spacing: 0.01em;
}

.toolkit-tile__connected-dot {
  position: absolute;
  top: -3px;
  right: -3px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #3ac47d;
  box-shadow: 0 0 0 2px #ffffff;
}

.toolkit-browse-btn {
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 4px;
  padding: 5px 12px;
  background: #f2ecfb;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #6c30cc;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.toolkit-browse-btn:hover {
  background: #e6dafa;
  border-color: #6c30cc;
}

.toolkit-browse-btn .el-icon-search {
  font-size: 12px;
}

.dialog-helper {
  font-size: 11px;
  font-weight: 500;
  color: #8a8a8a;
  margin-top: 6px;
}

.interpretation-alert {
  background: #f2ecfb;
  border: 1px solid #e6dafa;
  border-radius: 8px;
  color: #6c30cc;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
  padding: 10px 12px;
}

.interpretation-card {
  background: #fafbfc;
  border: 1px solid #e2e4ea;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.interpretation-card__summary {
  font-size: 14px;
  font-weight: 500;
  color: #1a1a1a;
  line-height: 1.5;
}

.interpretation-card__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.clarification-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: #f2ecfb;
  color: #6c30cc;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: background 0.15s;
}

.clarification-chip:hover {
  background: #e6dafa;
}

.interpretation-card__drift {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #fff7e4;
  border: 1px solid #f5dfa2;
  border-radius: 8px;
  padding: 10px 12px;
}
.interpretation-card__drift .happily-icon {
  color: #a4720a;
  flex-shrink: 0;
  margin-top: 1px;
}
.interpretation-card__drift-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.interpretation-card__drift-title {
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  font-weight: 800;
  color: #6a4a02;
  line-height: 1.35;
}
.interpretation-card__drift-title code {
  font-family: 'JetBrains Mono', 'Menlo', 'Monaco', monospace;
  font-size: 11px;
  background: rgba(247, 185, 36, 0.18);
  padding: 1px 5px;
  border-radius: 4px;
  border: 1px solid rgba(247, 185, 36, 0.4);
  color: #6a4a02;
  font-weight: 700;
}
.interpretation-card__drift-sub {
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #8a6a2a;
  line-height: 1.5;
}

.interpretation-card__params {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #ffffff;
  border: 1px solid #eef0f5;
  border-radius: 8px;
}
.interpretation-card__params-label {
  margin: 0;
}
.interpretation-card__params-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.recipe-param-chip {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  background: #fafbfd;
  border: 1px solid #e2e4ea;
  border-radius: 999px;
  padding: 3px 11px 3px 9px;
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  line-height: 1.35;
  max-width: 100%;
}
.recipe-param-chip__key {
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-size: 10px;
  color: #8a8a8a;
  flex-shrink: 0;
}
.recipe-param-chip__val {
  font-weight: 700;
  color: #1f1f1f;
  font-family: 'JetBrains Mono', 'Menlo', 'Monaco', monospace;
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 320px;
}

.interpretation-card__source-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #4a4a4a;
}

.interpretation-card__source-label {
  font-weight: 700;
  color: #8a8a8a;
}

.connection-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
}

.connection-block__copy {
  font-size: 13px;
  color: #4a4a4a;
  margin: 0;
}

.connection-block__waiting {
  font-size: 11px;
  color: #8a8a8a;
  font-style: italic;
}

.connection-active {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #fafbfc;
  border: 1px solid #e2e4ea;
  border-radius: 8px;
}

.connection-active__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 auto;
  min-width: 0;
}

.connection-active__label {
  font-size: 13px;
  font-weight: 700;
  color: #1a1a1a;
}

.connection-active__sub {
  font-size: 11px;
  color: #626262;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.connection-active__dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #00b383;
}

.connection-active__switch {
  font-family: inherit;
  background: #ffffff;
  border: 1px solid #e2e4ea;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: #6c30cc;
  cursor: pointer;
}

.connection-active__switch:hover {
  border-color: #6c30cc;
  background: #f2ecfb;
}

.preview-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preview-block__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.preview-block__diagnose {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff7e4;
  color: #a4720a;
  border: 1px solid #f5dfa2;
  border-radius: 999px;
  padding: 3px 11px 3px 9px;
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1;
  cursor: pointer;
  transition: background 140ms ease, color 140ms ease,
              border-color 140ms ease, box-shadow 140ms ease;
}
.preview-block__diagnose:hover {
  background: #fff0c4;
  border-color: #e8c46c;
  box-shadow: 0 1px 3px rgba(167, 117, 12, 0.12);
}

.preview-block__mismatch {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #fff7e4;
  border: 1px solid #f5dfa2;
  border-radius: 8px;
  padding: 10px 12px;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  width: 100%;
  transition: background 140ms ease, border-color 140ms ease,
              box-shadow 140ms ease, transform 140ms ease;
}
.preview-block__mismatch:hover {
  background: #fff0c4;
  border-color: #e8c46c;
  box-shadow: 0 2px 6px rgba(167, 117, 12, 0.12);
  transform: translateY(-0.5px);
}
.preview-block__mismatch .happily-icon {
  color: #a4720a;
  flex-shrink: 0;
  margin-top: 1px;
}
.preview-block__mismatch-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.preview-block__mismatch-title {
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  font-weight: 800;
  color: #6a4a02;
  letter-spacing: -0.005em;
  line-height: 1.35;
}
.preview-block__mismatch-sub {
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #8a6a2a;
  line-height: 1.45;
}
.preview-block__mismatch-cta {
  flex-shrink: 0;
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #a4720a;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(247, 185, 36, 0.22);
  border: 1px solid rgba(247, 185, 36, 0.4);
  align-self: center;
}
.preview-block__mismatch:hover .preview-block__mismatch-cta {
  background: rgba(247, 185, 36, 0.32);
}

.preview-block__value {
  font-size: 22px;
  font-weight: 900;
  color: #1a1a1a;
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.preview-block__unit {
  font-size: 14px;
  font-weight: 700;
  color: #8a8a8a;
}

.preview-block__note-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

/* Quote treatment matching .aq-response-quote (tinted block, no left bar). */
.preview-block__note {
  flex: 1;
  font-size: 13px;
  color: #4a4a4a;
  font-style: italic;
  padding: 8px 12px;
  background: #f7f8fb;
  border-radius: 8px;
  line-height: 1.5;
}

.preview-block__meter {
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.preview-block__meter-track {
  width: 100%;
  height: 6px;
  background: #eef0f5;
  border-radius: 999px;
  overflow: hidden;
}

.preview-block__meter-fill {
  height: 100%;
  background: #6c30cc;
  border-radius: 999px;
  transition: width 280ms ease-out;
}

.preview-block__meter-caption {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6c30cc;
}

.preview-block__meter-target {
  color: #8a8a8a;
}

.preview-block__samples {
  margin-top: 12px;
  background: #fafbfc;
  border: 1px solid #e2e4ea;
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.preview-block__samples-head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.preview-block__samples-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.preview-block__sample {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  border-top: 1px dashed #e2e4ea;
  font-size: 12px;
  color: #4a4a4a;
}

.preview-block__sample:first-child {
  border-top: none;
}

.preview-block__sample-label {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 12px;
}

.preview-block__sample-value {
  font-weight: 700;
  color: #1a1a1a;
  flex: 0 0 auto;
}

.preview-block__collapse {
  margin-top: 4px;
}

.preview-detail-grid {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
}

.preview-detail-grid__row {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 12px;
  padding: 4px 0;
}

.preview-detail-grid__key {
  font-weight: 700;
  color: #8a8a8a;
}

.preview-detail-grid__val {
  color: #1a1a1a;
  word-break: break-word;
}

.preview-detail-grid__mono {
  font-family: 'SFMono-Regular', Consolas, Menlo, monospace;
  font-size: 11px;
  background: #fafbfd;
  padding: 1px 6px;
  border-radius: 4px;
  border: 1px solid #e2e4ea;
}

.preview-block__warnings {
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.preview-warning {
  background: #fff8ec;
  border: 1px solid #f5dfa2;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  color: #4a4a4a;
}

.preview-warning--button {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  transition: background 140ms ease, border-color 140ms ease,
              box-shadow 140ms ease;
}
.preview-warning--button:hover {
  background: #fff2d0;
  border-color: #e8c46c;
  box-shadow: 0 1px 4px rgba(167, 117, 12, 0.10);
}
.preview-warning__text {
  flex: 1;
  min-width: 0;
}
.preview-warning__cta {
  flex-shrink: 0;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #a4720a;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(247, 185, 36, 0.18);
}

.preview-loading {
  font-size: 12px;
  color: #8a8a8a;
}

.preview-empty {
  font-size: 12px;
  color: #8a8a8a;
  font-style: italic;
}

.notification-checks {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.notification-people {
  margin-top: 4px;
}

.notification-fineprint {
  font-size: 11px;
  font-weight: 500;
  color: #8a8a8a;
  font-style: italic;
  margin-top: 4px;
}

.kr-action-icon {
  font-size: 14px;
  color: #909399;
  cursor: pointer;
  transition: color 0.15s;
}

.kr-action-icon:hover {
  color: #6c30cc;
}

.kr-automation-dialog__footer {
  display: flex;
  align-items: center;
  gap: 8px;
}

.kr-automation-dialog__footer-spacer {
  flex: 1;
}

.kr-automation-dialog__manage-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 0;
  padding: 4px 6px;
  margin-left: 2px;
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #8a8a8a;
  cursor: pointer;
  border-radius: 6px;
  transition: color 140ms ease, background-color 140ms ease;
}
.kr-automation-dialog__manage-link:hover {
  color: #6c30cc;
  background-color: #f5f5fa;
}

.recipe-fade-enter-active {
  transition: opacity 200ms ease-out, transform 200ms ease-out;
}
.recipe-fade-enter {
  opacity: 0;
  transform: translateY(4px);
}
.recipe-fade-enter-to {
  opacity: 1;
  transform: translateY(0);
}
</style>
